import { Box, Button, Typography, Grid, Alert } from "@mui/material";
import { useContext, useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import {
	GeoFileType,
	GeoJSONDataMap,
	GroupedGeoJSONFiles,
	FileOptionTypes,
	GeoJSONOption,
} from "../../utilities/types";
import { UserContext } from "../../hooks/UserContext";
import { PropertyWithExamples, FeatureStats } from "./ImportTable";
import ImportTableWithMap from "./ImportTable/ImportTableWithMap";
import { PropertyMappings } from "./GeoData/PropertyMappingSelector";
import { FeatureCollection, GeoJsonProperties, Geometry, Polygon, MultiPolygon, Point, Feature } from "geojson";
import { Job, defaultJobForImport } from "../../api";
import { AutoAwesome } from "@mui/icons-material";
import { GeoData } from "@rogoag/shared-ts";
import GoogleMapComponent from "../jobsdashboard/GoogleMapComponent";
import { GeoTypeSelectorGroup } from "./GeoData/GeoTypeSelectorGroup";
import { generateFileNames, ImportFormProps } from "./ImportHelpers";

export function GeoDataImportForm(props: ImportFormProps) {
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const [jobData, setJobData] = useState<Job[]>([]);
	const importTableRef = useRef<HTMLDivElement>(null);
	const [selectableGeoData, setSelectableGeoData] = useState<GroupedGeoJSONFiles>({});
	const [originalShapefiles, setOriginalShapefiles] = useState<File[]>([]);
	const [geoDataMap, setGeoDataMap] = useState<GeoJSONDataMap>(new Map());
	const [shouldShowMap, setShouldShowMap] = useState(false);
	const [mapBoundaries, setMapBoundaries] = useState<
		Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>>
	>({});
	const [mapZones, setMapZones] = useState<
		Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>>
	>({});
	const [mapPoints, setMapPoints] = useState<Record<string, FeatureCollection<Point>>>({});
	const [extractedHeaderProperties, setExtractedHeaderProperties] = useState<PropertyWithExamples[]>([]);
	const [allPropertyMappings, setAllPropertyMappings] = useState<Record<string, PropertyMappings>>({});
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);
	const [unmatchedFeatures, setUnmatchedFeatures] = useState<{
		centroids: string[];
		cores: string[];
		zones: string[];
	}>({ centroids: [], cores: [], zones: [] });

	// Callback to collect property mappings from each file group
	const handlePropertyMappingsChange = (groupKey: string, mappings: PropertyMappings) => {
		setAllPropertyMappings((prev) => ({
			...prev,
			[groupKey]: mappings,
		}));
	};

	// Auto-scroll to ImportTableWithMap when jobData populates
	useEffect(() => {
		if (jobData.length > 0 && importTableRef.current) {
			// Small delay to ensure the component has rendered
			setTimeout(() => {
				importTableRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
			}, 100);
		}
	}, [jobData.length]);

	function filesCount(type: GeoFileType) {
		const count = Object.values(selectableGeoData).reduce((count, types) => {
			return count + (types[type] ? types[type].length : 0);
		}, 0);
		return count;
	}

	const boundaryCount = filesCount("Boundary");
	// const centroidsCount = filesCount("Centroids");
	// const coresCount = filesCount("Cores");
	// const zonesCount = filesCount("Zones");
	const shouldShowExtractButton = boundaryCount > 0; // && (centroidsCount > 0 || coresCount > 0 || zonesCount > 0);

	return (
		<>
			<Grid alignItems="stretch" alignContent="stretch" container maxWidth="md" margin="auto" spacing={1}>
				{loading && (
					<Grid item xs={12}>
						<Typography>Loading...</Typography>
					</Grid>
				)}
				<GeoTypeSelectorGroup
					geoDataMap={geoDataMap}
					setGeoDataMap={setGeoDataMap}
					selectableGeoData={selectableGeoData}
					setSelectableGeoData={setSelectableGeoData}
					originalShapefiles={originalShapefiles}
					setOriginalShapefiles={setOriginalShapefiles}
					loading={loading}
					setLoading={setLoading}
					onPropertyMappingsChange={handlePropertyMappingsChange}
					onClear={() => {
						setJobData([]);
						setSelectableGeoData({});
						setExtractedHeaderProperties([]);
						setAllPropertyMappings({});
						setUnmatchedFeatures({ centroids: [], cores: [], zones: [] });
					}}
				/>

				<Grid item xs={12} width={"100%"}>
					{/* Show unmatched features warning if any exist */}
					{(unmatchedFeatures.centroids.length > 0 ||
						unmatchedFeatures.cores.length > 0 ||
						unmatchedFeatures.zones.length > 0) && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							<Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
								Warning: Some{" "}
								{(() => {
									const types = [];
									if (unmatchedFeatures.centroids.length > 0) types.push("grid samples");
									if (unmatchedFeatures.cores.length > 0) types.push("zone cores");
									if (unmatchedFeatures.zones.length > 0) types.push("sample zones");

									if (types.length === 1) return types[0];
									if (types.length === 2) return `${types[0]} and ${types[1]}`;
									return `${types[0]}, ${types[1]}, and ${types[2]}`;
								})()}{" "}
								could not be matched to a field boundary.
							</Typography>

							{unmatchedFeatures.centroids.length > 0 && (
								<Box sx={{ mb: 2 }}>
									<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
										{unmatchedFeatures.centroids.length} unmatched grid sample
										{unmatchedFeatures.centroids.length !== 1 ? "s" : ""} from:
									</Typography>
									<ul style={{ margin: 0, paddingLeft: "20px" }}>
										{unmatchedFeatures.centroids.map((path, index) => (
											<li key={index}>
												<Typography
													variant="body2"
													component="span"
													sx={{ fontSize: "0.85rem", color: "text.secondary" }}
												>
													{path}
												</Typography>
											</li>
										))}
									</ul>
								</Box>
							)}

							{unmatchedFeatures.cores.length > 0 && (
								<Box sx={{ mb: 2 }}>
									<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
										{unmatchedFeatures.cores.length} unmatched zone core
										{unmatchedFeatures.cores.length !== 1 ? "s" : ""} from:
									</Typography>
									<ul style={{ margin: 0, paddingLeft: "20px" }}>
										{unmatchedFeatures.cores.map((path, index) => (
											<li key={index}>
												<Typography
													variant="body2"
													component="span"
													sx={{ fontSize: "0.85rem", color: "text.secondary" }}
												>
													{path}
												</Typography>
											</li>
										))}
									</ul>
								</Box>
							)}

							{unmatchedFeatures.zones.length > 0 && (
								<Box sx={{ mb: unmatchedFeatures.centroids.length > 0 || unmatchedFeatures.cores.length > 0 ? 0 : 0 }}>
									<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
										{unmatchedFeatures.zones.length} unmatched sample zone
										{unmatchedFeatures.zones.length !== 1 ? "s" : ""} from:
									</Typography>
									<ul style={{ margin: 0, paddingLeft: "20px" }}>
										{unmatchedFeatures.zones.map((path, index) => (
											<li key={index}>
												<Typography
													variant="body2"
													component="span"
													sx={{ fontSize: "0.85rem", color: "text.secondary" }}
												>
													{path}
												</Typography>
											</li>
										))}
									</ul>
								</Box>
							)}
						</Alert>
					)}

					{/* Extract Job Data button */}
					<Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
						{shouldShowExtractButton && (
							<Button color="primary" variant="contained" onClick={parseGeoDataIntoJobs} sx={{ mr: 1, ml: 1 }}>
								<AutoAwesome className="icon-in-button" />
								Extract Job Data
							</Button>
						)}
					</Box>
				</Grid>
			</Grid>
			{shouldShowMap && (
				<Box sx={{ mt: 2, mb: 2, width: "100%" }}>
					<GoogleMapComponent boundaries={mapBoundaries} zones={mapZones} points={mapPoints} height="500px" />
				</Box>
			)}
			{jobData.length > 0 && (
				<Box ref={importTableRef} sx={{ width: "100%" }}>
					<ImportTableWithMap
						jobs={jobData}
						updateJobs={(jobs) => {
							setJobData(jobs);
						}}
						deals={props.deals}
						allDeals={props.deals}
						setLoading={(task: string | boolean) => {
							setLoading(!!task);
						}}
						allOptions={{ Boundary: [], Zones: [], Centroids: [], Cores: [], Unknown: [] }}
						resetForm={() => {
							setJobData([]);
							setOriginalShapefiles([]);
							setSelectableGeoData({});
							setGeoDataMap(new Map());
							setShouldShowMap(false);
							setMapBoundaries({});
							setMapZones({});
							setMapPoints({});
							setExtractedHeaderProperties([]);
							setAllPropertyMappings({});
							setFeatureStats(undefined);
							setUnmatchedFeatures({ centroids: [], cores: [], zones: [] });
						}}
						user={userContext}
						allowMap={true}
						showHeaderSelectors={true}
						headerProperties={extractedHeaderProperties}
						featureStats={featureStats}
						selectedCompany={props.selectedCompany ?? undefined}
						salespersons={props.salespersons ?? []}
						branches={props.branches ?? []}
						lastCropOptions={props.lastCropOptions}
						nextCropOptions={props.nextCropOptions}
					/>
				</Box>
			)}
		</>
	);

	function extractAllPropertiesFromGeoData(
		geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
	): Array<{ name: string; examples: string[] }> {
		const propertyMap = new Map<string, Set<string>>();

		// Collect unique values for each property
		for (const geoJson of geoJsons) {
			for (const feature of geoJson.features || []) {
				if (feature.properties) {
					Object.entries(feature.properties).forEach(([key, value]) => {
						if (value !== null && value !== undefined && String(value).trim() !== "") {
							if (!propertyMap.has(key)) {
								propertyMap.set(key, new Set<string>());
							}
							// Add the string representation of the value
							propertyMap.get(key)!.add(String(value));
						}
					});
				}
			}
		}

		// Convert to array format with up to 3 examples per property
		const result: Array<{ name: string; examples: string[] }> = [];
		for (const [propertyName, values] of Array.from(propertyMap.entries())) {
			const examples = Array.from(values).slice(0, 3) as string[];
			result.push({ name: propertyName, examples });
		}

		// Sort by property name
		return result.sort((a, b) => a.name.localeCompare(b.name));
	}

	// Extract property value from GeoJSON features based on property mappings
	function extractPropertyValueFromGeoData(
		geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
		propertyName: string | null,
	): string {
		if (!propertyName) return "";

		for (const geoJson of geoJsons) {
			for (const feature of geoJson.features || []) {
				if (feature.properties && feature.properties[propertyName]) {
					const value = feature.properties[propertyName];
					if (value !== null && value !== undefined && String(value).trim() !== "") {
						let str = String(value);
						if (str.toLowerCase().endsWith(".zip")) {
							str = str.slice(0, -4);
						}
						return str;
					}
				}
			}
		}

		return "";
	}

	async function parseGeoDataIntoJobs() {
		setJobData([]);
		setUnmatchedFeatures({ centroids: [], cores: [], zones: [] });

		const matchingGeodata = toast.loading("Processing geodata...", { progress: 0 });

		// Add group key to each feature before flattening
		// Helper to clone and add __groupKey to features
		function extractAndTagGeoJsons(type: GeoFileType) {
			return Object.entries(selectableGeoData).flatMap(([groupKey, data]) =>
				(data[type] || []).map((geojson) => {
					const cloned: FeatureCollection = JSON.parse(JSON.stringify(geojson));
					cloned.features.forEach((feature: any) => {
						if (!feature.properties) feature.properties = {};
						feature.properties.__groupKey = groupKey;
					});
					if (type == "Boundary") {
						const groupKey = cloned?.features?.[0]?.properties?.__groupKey;
						const relevantMappings = groupKey ? allPropertyMappings[groupKey] : null;

						// Add the extracted values to the cloned feature's properties
						cloned.features.forEach((feature: Feature) => {
							if (!feature.properties) feature.properties = {};

							if (relevantMappings != null) {
								// Check if a custom unique key property is specified
								if (relevantMappings.uniqueKey) {
									// user-defined property from Advanced Options
									const uniqueKeyValue = feature.properties[relevantMappings.uniqueKey];
									if (uniqueKeyValue !== null && uniqueKeyValue !== undefined) {
										feature.properties.__uniqueJobKey = String(uniqueKeyValue);
									}
								} else {
									// Fall back to concatenated key if no custom unique key is specified
									const growerName = relevantMappings.grower ? feature.properties[relevantMappings.grower] : "";
									const farmName = relevantMappings.farm ? feature.properties[relevantMappings.farm] : "";
									const fieldName = relevantMappings.field ? feature.properties[relevantMappings.field] : "";
									const filePath = feature.properties.FilePath || "";
									const uniqueKey = `${growerName}${farmName}${fieldName}${filePath}`;
									if (fieldName) feature.properties.__uniqueJobKey = uniqueKey;
								}
							}
						});

						return GeoData.mergeBoundaryPolygonsIntoMultiPolygon(cloned);
					}
					return cloned;
				}),
			);
		}

		const bndGeoJsons = extractAndTagGeoJsons("Boundary");
		const znsGeoJsons = extractAndTagGeoJsons("Zones");
		const cdsGeoJsons = extractAndTagGeoJsons("Centroids");
		const crsGeoJsons = extractAndTagGeoJsons("Cores");

		async function updateToast(completed: number, total: number, message: string = ""): Promise<void> {
			if (matchingGeodata) {
				toast.update(matchingGeodata, {
					render: message || `Processing ${completed}/${total} boundaries... `,
					progress: completed / total,
				});
				await new Promise((resolve) => setTimeout(resolve, 0));
			} else {
				console.warn("matchingGeodata toast is not defined. Cannot update progress.");
			}
		}

		const matchResults = await GeoData.match(bndGeoJsons, znsGeoJsons, cdsGeoJsons, crsGeoJsons, updateToast);

		// Calculate feature stats - count actual features, not FeatureCollections
		const stats: FeatureStats = {
			boundaries: matchResults.matches.length,
			zones: matchResults.matches.filter((m) => m.zones).length,
			centroids: matchResults.matches.filter((m) => m.centroids).length,
			cores: matchResults.matches.filter((m) => m.cores).length,
		};
		setFeatureStats(stats);

		// Extract unique file paths from unmatched features
		const extractFilePaths = (features: any[]): string[] => {
			const paths = new Set<string>();
			features.forEach((feature) => {
				if (feature?.properties?.FilePath) {
					paths.add(feature.properties.FilePath);
				}
			});
			return Array.from(paths);
		};

		const unmatchedFilePaths = {
			centroids: extractFilePaths(matchResults.unmatched.centroids),
			cores: extractFilePaths(matchResults.unmatched.cores),
			zones: extractFilePaths(matchResults.unmatched.zones),
		};

		setUnmatchedFeatures(unmatchedFilePaths);

		// Show warning if there are unmatched features but don't return early
		if (matchResults.unmatched.centroids.length > 0) {
			toast.warning(`Unable to match ${matchResults.unmatched.centroids.length} centroids to boundaries.`);
		}
		if (matchResults.unmatched.cores.length > 0) {
			toast.warning(`Unable to match ${matchResults.unmatched.cores.length} cores to boundaries.`);
		}
		if (matchResults.unmatched.zones.length > 0) {
			toast.warning(`Unable to match ${matchResults.unmatched.zones.length} zones to boundaries.`);
		}

		if (matchResults.matches.length === 0) {
			toast.error("Unable to process geodata. Please contact support@rogoag.com.");
			return;
		}

		// Extract all properties from all GeoJSON features for header dropdowns
		const allMatchedGeoJSONs: FeatureCollection<Geometry, GeoJsonProperties>[] = [];
		for (const match of matchResults.matches) {
			if (match.boundary) allMatchedGeoJSONs.push(match.boundary);
			if (match.zones) allMatchedGeoJSONs.push(match.zones);
			if (match.centroids) allMatchedGeoJSONs.push(match.centroids);
			if (match.cores) allMatchedGeoJSONs.push(match.cores);
		}

		if (allMatchedGeoJSONs.length > 0) {
			const properties = extractAllPropertiesFromGeoData(allMatchedGeoJSONs);
			setExtractedHeaderProperties(properties);
		}

		// Helper function to clean __groupKey property from GeoJSON
		const removeTag = (geojson: FeatureCollection<Geometry, GeoJsonProperties> | undefined) => {
			if (!geojson) return undefined;
			const cleaned = JSON.parse(JSON.stringify(geojson));
			cleaned.features.forEach((feature: any) => {
				if (feature.properties && feature.properties.__groupKey) {
					delete feature.properties.__groupKey;
				}
			});
			return cleaned;
		};

		const newJobs: Job[] = [];
		for (const match of matchResults.matches) {
			const bndGeoJSON = match.boundary;
			const znsGeoJSON = match.zones;
			const cdsGeoJSON = match.centroids;
			const crsGeoJSON = match.cores;
			let ptsGeoJSON: FeatureCollection<Geometry, GeoJsonProperties> | undefined = undefined;
			if (cdsGeoJSON && crsGeoJSON) {
				ptsGeoJSON = GeoData.merge([cdsGeoJSON, crsGeoJSON]);
			} else {
				ptsGeoJSON = cdsGeoJSON || crsGeoJSON || undefined;
			}

			// Clean ptsGeoJSON after merging
			const cleanPtsGeoJSON = removeTag(ptsGeoJSON);
			if (!bndGeoJSON) {
				toast.error("No boundary found for a field.");
				return;
			}

			const allGeoJSONs = [bndGeoJSON, cdsGeoJSON, crsGeoJSON, znsGeoJSON].filter(Boolean) as FeatureCollection<
				Geometry,
				GeoJsonProperties
			>[];

			// Extract property values using collected property mappings
			// Get the group key from the boundary features to find the correct property mappings
			const groupKey = bndGeoJSON?.features?.[0]?.properties?.__groupKey;
			const relevantMappings = groupKey ? allPropertyMappings[groupKey] : null;

			const fieldName = relevantMappings ? extractPropertyValueFromGeoData(allGeoJSONs, relevantMappings.field) : "";
			const farmName = relevantMappings ? extractPropertyValueFromGeoData(allGeoJSONs, relevantMappings.farm) : "";
			const growerName = relevantMappings ? extractPropertyValueFromGeoData(allGeoJSONs, relevantMappings.grower) : "";
			const fieldId = relevantMappings ? extractPropertyValueFromGeoData(allGeoJSONs, relevantMappings.fieldId) : "";

			const { bndName, znsName, cdsName, crsName } = generateFileNames(growerName, farmName, fieldName);

			// Clean up __groupKey property before creating files
			const cleanBndGeoJSON = removeTag(bndGeoJSON)!;
			const cleanZnsGeoJSON = removeTag(znsGeoJSON);
			const cleanCdsGeoJSON = removeTag(cdsGeoJSON);
			const cleanCrsGeoJSON = removeTag(crsGeoJSON);

			const bndOption: GeoJSONOption = { name: bndName, geoJSON: cleanBndGeoJSON };
			const znsOptions: GeoJSONOption[] = cleanZnsGeoJSON ? [{ name: znsName, geoJSON: cleanZnsGeoJSON }] : [];
			const cdsOptions: GeoJSONOption[] = cleanCdsGeoJSON ? [{ name: cdsName, geoJSON: cleanCdsGeoJSON }] : [];
			const crsOptions: GeoJSONOption[] = cleanCrsGeoJSON ? [{ name: crsName, geoJSON: cleanCrsGeoJSON }] : [];

			const depth = props.selectedDeals.length ? props.selectedDeals[0].depth || 0 : 0;
			const density = props.selectedDeals.length ? props.selectedDeals[0].density || 0 : 0;
			const frequencyOfMicros = props.selectedDeals.length ? props.selectedDeals[0].frequencyOfMicros || 0 : 0;
			const testPackage = props.selectedDeals.length ? props.selectedDeals[0].testPackage || "" : "";

			const newJob: Job = defaultJobForImport(
				fieldName,
				depth,
				density,
				growerName,
				frequencyOfMicros,
				testPackage,
				farmName,
			);

			newJob.jobFlags = ["Bulk Upload"];
			newJob.dealIds = props.selectedDeals.length ? [props.selectedDeals[0].id] : [];
			newJob.branchIds = props.selectedBranches.map((branch) => branch.id);
			newJob.fieldId = String(fieldId);
			newJob.submitterEmail = props.user?.email || "";
			newJob.submitterName = props.user?.name || "";

			newJob.fileOptions = {
				boundaryOptions: [{ type: FileOptionTypes.Boundary, options: [bndOption] }],
				pointsOptions: [
					{ type: FileOptionTypes.Centroids, options: cdsOptions },
					{ type: FileOptionTypes.Cores, options: crsOptions },
				],
				zonesOptions: [{ type: FileOptionTypes.Zones, options: znsOptions }],
				boundaryGeoJSON: cleanBndGeoJSON,
				centroidsGeoJSON: cleanCdsGeoJSON,
				coresGeoJSON: cleanCrsGeoJSON,
				zonesGeoJSON: cleanZnsGeoJSON,
			};

			// Set files with the initially selected files
			newJob.files = {
				boundaryGeoJSON: cleanBndGeoJSON,
				pointsGeoJSON: cleanPtsGeoJSON,
				zonesGeoJSON: cleanZnsGeoJSON,
			};
			newJobs.push(newJob);
		}
		setJobData(newJobs);

		populateMap(bndGeoJsons, znsGeoJsons, cdsGeoJsons, crsGeoJsons);

		toast.dismiss(matchingGeodata);
	}

	function populateMap(
		boundaryGeoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
		zoneGeoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
		centroidGeoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
		coresGeoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
	) {
		const boundaries: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>> = {};
		const zones: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>> = {};
		const points: Record<string, FeatureCollection<Point>> = {};

		boundaryGeoJsons.forEach((geoJson, index) => {
			if (geoJson) {
				boundaries[`boundary-${index}`] = geoJson as FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>;
			}
		});

		zoneGeoJsons.forEach((geoJson, index) => {
			if (geoJson) {
				zones[`zone-${index}`] = geoJson as FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>;
			}
		});

		centroidGeoJsons.forEach((geoJson, index) => {
			if (geoJson) {
				points[`centroid-${index}`] = geoJson as FeatureCollection<Point>;
			}
		});

		coresGeoJsons.forEach((geoJson, index) => {
			if (geoJson) {
				points[`core-${index}`] = geoJson as FeatureCollection<Point>;
			}
		});

		setMapBoundaries(boundaries);
		setMapZones(zones);
		setMapPoints(points);
	}
}
